import {
  getUserDownloadStatus,
  createDataDownload,
  DownloadResponse,
  createDownloadNotification,
  deleteDownloadNotification
} from '../../../../services/endpoints/data'
import { LoadIndicator } from 'devextreme-react'
import React, { useEffect, useState } from 'react'
import { Progress, Container, Row, Col } from 'reactstrap'
import useAsyncService from '../../../../hooks/useAsyncService'
import { NetworkRequestError } from '../../../../services/lib'
import ICSErrorCard from '../../../../components/ICSErrorCard/ICSErrorCard'
import { useAppSelector } from '../../../../hooks/useAppSelector'
import { downloadFile } from '../../../../services/endpoints/downloader'
import RenderDataModal from '../RenderViewModal/RenderViewModal'
import { formatError } from '../../../../redux/lib/errors'
import { getFileSize } from '../../../../utils/helpers'
import './DownloadModal.css'

interface DownloadModalProps {
  onClose: () => void
}
const DownloadStatusModal: React.FC<DownloadModalProps> = ({ onClose }) => {
  const [isComplete, setIsComplete] = useState(false)
  const [userDownload, setUserDownload] = useState<DownloadResponse | null>(
    null
  )

  const getBody = () => {
    if (!userDownload) {
      return <_CreateDownload onCreated={setUserDownload} />
    } else if (!isComplete) {
      return (
        <_ShowProgress
          onCompleted={(name, size) => {
            setIsComplete(true)
            setUserDownload({ ...userDownload, name, size })
          }}
          userDownloadId={userDownload.id}
        />
      )
    } else {
      return <_ShowUserDownload userDownload={userDownload} />
    }
  }

  return (
    <RenderDataModal onClose={onClose} title="Create Download">
      <Container className="create-download-container">
        <Row className="create-download-">
          <Col>{getBody()}</Col>
        </Row>
      </Container>
    </RenderDataModal>
  )
}

interface _CreateDownloadProps {
  onCreated: (response: DownloadResponse) => void
}
const _CreateDownload: React.FC<_CreateDownloadProps> = ({ onCreated }) => {
   const pref = useAppSelector(state => state.userPreference.data)
  const versions = useAppSelector((State) => State.data.versions)
  const { serviceValue, serviceError, setServiceArgs } = useAsyncService({
    serviceCall: createDataDownload,
    delay: 0,
    initialArgs: null
  })

  useEffect(() => {
    const selectedIds = Object.entries(versions).reduce<string[]>(
      (a, [id, v]) => {
        if (v.selected) a.push(id)

        return a
      },
      []
    )
    const flattener = pref.flatten?.val == "true"
    const timezoneOffest = pref.timezone?.val
    setServiceArgs({selectedIds, flattener: flattener, timezoneOffset: timezoneOffest})
  }, [versions])

  useEffect(() => {
    if (serviceValue) {
      onCreated(serviceValue)
    }
  }, [serviceValue])

  if (serviceError) {
    return <ICSErrorCard code={'error'} message={serviceError} fields={[]} />
  } else if (serviceValue) {
    return <>Completed creating download</>
  } else {
    return <LoadIndicator />
  }
}

const _ShowProgress: React.FC<{
  userDownloadId: number
  onCompleted: (name: string, size: number) => void
}> = ({ userDownloadId, onCompleted }) => {
  const { percentage, completed, total, error, complete, name, size } =
    useDownloadStatus(userDownloadId)

  useEffect(() => {
    if (complete) {
      onCompleted(name, size)
    }
  }, [complete, name, size, onCompleted])

  if (error) {
    return <ICSErrorCard code={'error'} message={error.message} fields={[]} />
  }

  return (
    <div>
      <div className="progress-group">
        <p className="progress-label">Current File Progress {percentage}%</p>
        <Progress value={percentage} animated>
          <span>{percentage}</span>
        </Progress>
      </div>
      <div className="progress-group">
        <p className="progress-label">
          Files processed: {completed} / {total}
        </p>
        <Progress value={total ? (completed / total) * 100 : 0}>
          <span>
            {completed} / {total}
          </span>
        </Progress>
      </div>
      <p className="progress-btm-para">
        Data compression can take several minutes for large files. When
        complete, the prepared download will be listed in your downloads area on
        the dashboard or your account homepage.
      </p>
      <_NotificationCheckbox userDownloadId={userDownloadId} />
      <div className="settings">
        <span className="glyphicons glyphicons-cogwheels"></span>
        <span className="settings-text">Settings</span>
      </div>
      <div className="cross-row">
        <span>No directories inside zip file: </span>
        <span className="glyphicons glyphicons-remove text-red"></span>
      </div>
      <div className="cross-row">
        <span>Convert filenames to local timezone: </span>
        <span className="glyphicons glyphicons-remove text-red"></span>
      </div>
    </div>
  )
}

const _ShowUserDownload: React.FC<{ userDownload: DownloadResponse }> = ({
  userDownload
}) => {
  const { serviceLoading, serviceError, setServiceArgs } = useAsyncService({
    initialArgs: null,
    serviceCall: downloadFile
  })

  useEffect(() => {
    if (!serviceLoading) setServiceArgs(null)
  }, [serviceLoading])

  if (serviceLoading) {
    return <LoadIndicator />
  } else if (serviceError) {
    return <ICSErrorCard code={'error'} message={serviceError} fields={[]} />
  } else {
    return (
      <div className="download-link-wrapper">
        <a
          className="download-link"
          onClick={() => setServiceArgs({ type: 'user', id: userDownload.id })}
        >
          {userDownload.name}
        </a>
        <span className="file-size">{getFileSize(userDownload.size)}</span>
      </div>
    )
  }
}

const _NotificationCheckbox: React.FC<{ userDownloadId: number }> = ({
  userDownloadId
}) => {
  const [checked, setChecked] = useState(false)
  const [preferenceId, setPreferenceId] = useState<number | null>(null)

  const userId = useAppSelector((State) => State.userPreference.userId)
  const { serviceValue, serviceError, setServiceArgs } = useAsyncService({
    serviceCall: createDownloadNotification,
    delay: 0,
    initialArgs: null
  })

  useEffect(() => {
    if (!userId) {
      console.error(
        `Unable to create notification for user downloadId: ${userDownloadId}`
      )
      return
    }

    const handleNotification = async () => {
      if (checked) {
        setServiceArgs({ userDownloadId, userId })
      } else if (!checked && preferenceId) {
        try {
          await deleteDownloadNotification(preferenceId)
          setPreferenceId(null)
        } catch (error) {
          console.error(
            `Unable to delete notification preference for user downloadId: ${userDownloadId}`,
            error
          )
        }
      }
    }

    handleNotification()
  }, [checked])

  useEffect(() => {
    if (serviceValue) {
      setPreferenceId(serviceValue.id)
    }
  }, [serviceValue])

  return (
    <div>
      <label className="download-label">
        <input
          type="checkbox"
          checked={checked}
          onChange={() => setChecked(!checked)}
        />
        <span>Notify me when the file is ready to download</span>
      </label>
    </div>
  )
}

const useDownloadStatus = (userDownloadId: number) => {
  const [status, setStatus] = useState({
    percentage: 0,
    completed: 0,
    total: 0,
    complete: false,
    name: '',
    size: 0
  })

  const [error, setError] = useState<NetworkRequestError | null>(null)

  useEffect(() => {
    const interval = setInterval(async () => {
      try {
        const res = await getUserDownloadStatus(userDownloadId)
        updatePercent(res)
      } catch (e) {
        setError(formatError(e))
      }
    }, 3000)

    return () => clearInterval(interval)
  }, [])

  const updatePercent = (data: DownloadResponse) => {
    if (!data.name) return

    if (data.name.startsWith('Compressing')) {
      const matches = data.name.match(
        /^\w+\s-?(\d{1,})%\s\/\s(\d{1,})\s\/\s(\d{1,})/
      )
      if (matches && matches.length === 4) {
        setStatus({
          ...status,
          percentage: parseInt(matches[1]),
          completed: parseInt(matches[2]),
          total: parseInt(matches[3]),
          name: data.name,
          size: data.size
        })
      } else {
        //todo - this is bad... error of some sort?
        console.error(
          `user download status isn't formated correctly - ${data.name}`
        )
      }
    } else {
      setStatus({
        ...status,
        percentage: 1000,
        completed: status.total,
        complete: true,
        name: data.name,
        size: data.size
      })
    }
  }
  return { ...status, error }
}

export default DownloadStatusModal
